import React, { useState } from "react";
import {
    Edit3, Trash2, Loader2, ExternalLink, Copy, CheckCircle2, Archive, FileEdit, Eye, FileDown, Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError, backendUrl } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { JobApplicantsDrawer } from "./JobApplicantsDrawer";

const STATUS_META = {
    published: { label: "Live", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
    pending_review: { label: "Pending", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
    draft: { label: "Draft", cls: "bg-slate-500/15 text-slate-600 dark:text-slate-400" },
    archived: { label: "Archived", cls: "bg-slate-500/15 text-slate-500 dark:text-slate-400" },
    rejected: { label: "Rejected", cls: "bg-rose-500/15 text-rose-600 dark:text-rose-400" },
};

const StatusPill = ({ status }) => {
    const m = STATUS_META[status] || { label: status || "—", cls: "bg-muted text-muted-foreground" };
    return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${m.cls}`}>
            {m.label}
        </span>
    );
};

const FILTERS = [
    { key: "all", label: "All" },
    { key: "published", label: "Live" },
    { key: "pending_review", label: "Pending" },
    { key: "draft", label: "Drafts" },
    { key: "archived", label: "Archived" },
    { key: "rejected", label: "Rejected" },
];

export const JobsTab = ({ jobs, loading, onEdit, onChanged }) => {
    const [copiedId, setCopiedId] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [exporting, setExporting] = useState(false);
    const [applicantsJob, setApplicantsJob] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [confirmText, setConfirmText] = useState("");

    const setStatus = async (job, newStatus) => {
        setBusyId(job.id);
        try {
            await api.patch(`/admin/jobs/${job.id}/status`, { status: newStatus });
            toast.success(
                newStatus === "published" ? "Job published." :
                newStatus === "draft" ? "Moved to Draft." :
                newStatus === "archived" ? "Job archived." :
                "Status updated."
            );
            onChanged();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusyId(null); }
    };

    const requestDelete = (job) => { setDeleteTarget(job); setConfirmText(""); };
    const confirmDelete = async () => {
        if (confirmText.trim() !== "DELETE") return;
        const job = deleteTarget;
        setBusyId(job.id);
        try {
            await api.delete(`/admin/jobs/${job.id}`);
            toast.success("Job deleted.");
            setDeleteTarget(null);
            setConfirmText("");
            onChanged();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusyId(null); }
    };

    const copyShareLink = async (job) => {
        const url = `${window.location.origin}/careers/${job.id}`;
        try {
            await navigator.clipboard.writeText(url);
            setCopiedId(job.id);
            toast.success("Share link copied to clipboard.");
            setTimeout(() => setCopiedId(null), 2500);
        } catch {
            toast.error("Could not copy link.");
        }
    };

    if (loading) return <Loader2 className="animate-spin" />;

    if (!jobs || jobs.length === 0) {
        return (
            <div className="card-soft p-10 text-center">
                <h3 className="font-heading font-semibold text-brand-navy dark:text-slate-100">No jobs posted yet.</h3>
                <p className="text-brand-muted text-sm mt-1">Click <span className="font-semibold">New Job</span> to publish your first role — it will go live at <code className="text-xs">/careers</code>.</p>
            </div>
        );
    }

    const counts = { all: jobs.length };
    for (const j of jobs) {
        const s = j.status || (j.is_active === false ? "archived" : "published");
        counts[s] = (counts[s] || 0) + 1;
    }
    const filtered = statusFilter === "all"
        ? jobs
        : jobs.filter((j) => (j.status || (j.is_active === false ? "archived" : "published")) === statusFilter);

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-1.5" data-testid="admin-jobs-filter-tabs">
                {FILTERS.map((t) => {
                    const n = counts[t.key] || 0;
                    const active = statusFilter === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setStatusFilter(t.key)}
                            data-testid={`admin-jobs-filter-${t.key}`}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                active
                                    ? "border-emerald-500 bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 font-semibold"
                                    : "border-border bg-background hover:bg-muted text-muted-foreground"
                            }`}
                        >
                            {t.label} <span className="ml-1 tabular-nums opacity-70">{n}</span>
                        </button>
                    );
                })}
                <button
                    onClick={async () => {
                        setExporting(true);
                        try {
                            const token = localStorage.getItem("simonara_admin_token");
                            const r = await fetch(backendUrl("/api/admin/jobs/export.csv"), { headers: { Authorization: `Bearer ${token}` } });
                            if (!r.ok) throw new Error(`HTTP ${r.status}`);
                            const blob = await r.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `simonara-jobs-${new Date().toISOString().slice(0, 10)}.csv`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            setTimeout(() => URL.revokeObjectURL(url), 60_000);
                            toast.success("Jobs CSV exported.");
                        } catch {
                            toast.error("Couldn't export the CSV. (Master admin only.)");
                        } finally {
                            setExporting(false);
                        }
                    }}
                    disabled={exporting || jobs.length === 0}
                    className="ml-auto text-xs px-3 py-1.5 rounded-full border border-sky-500/30 bg-sky-500/10 text-sky-700 hover:bg-sky-500/20 inline-flex items-center gap-1.5 disabled:opacity-60"
                    data-testid="admin-jobs-export-csv"
                    title="Export current job list to CSV (master admin only)"
                >
                    {exporting ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
                    Export CSV
                </button>
            </div>

            {filtered.length === 0 ? (
                <div data-testid="admin-jobs-filter-empty" className="rounded-2xl border border-dashed border-border bg-card/60 p-8 text-center text-sm text-muted-foreground">
                    No jobs in <strong>{statusFilter}</strong>. <button onClick={() => setStatusFilter("all")} className="ml-2 text-brand-green hover:underline">Show all</button>
                </div>
            ) : filtered.map((job) => {
                const status = job.status || (job.is_active === false ? "archived" : "published");
                const disabled = busyId === job.id;
                return (
                    <div key={job.id} data-testid={`admin-job-${job.id}`} className="card-soft p-5 flex flex-col md:flex-row md:items-center gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                {job.req_number && (
                                    <span
                                        className="text-[10px] uppercase tracking-wider font-mono font-semibold text-muted-foreground bg-muted/50 border border-border px-1.5 py-0.5 rounded"
                                        data-testid={`admin-job-req-${job.id}`}
                                        title="Requisition number"
                                    >
                                        {job.req_number}
                                    </span>
                                )}
                                <h3 className="font-heading font-semibold text-brand-navy dark:text-slate-100">{job.title}</h3>
                                <Badge variant="outline" className="text-xs">{job.employment_type}</Badge>
                                <Badge variant="outline" className="text-xs">{job.industry}</Badge>
                                <StatusPill status={status} />
                            </div>
                            <div className="text-sm text-brand-muted mt-1">{job.company} · {job.location} {job.salary ? `· ${job.salary}` : ""}</div>
                            {job.posted_by ? (
                                <div className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1.5 flex-wrap" data-testid={`admin-job-poster-${job.id}`}>
                                    <span className="opacity-70">Posted by</span>
                                    <span className="font-medium text-foreground">
                                        {job.posted_by.name || job.posted_by.email}
                                    </span>
                                    {job.posted_by.role && (
                                        <span className="text-[10px] uppercase tracking-wider bg-slate-500/10 text-slate-700 dark:text-slate-300 border border-slate-500/30 px-1.5 py-0.5 rounded-full">
                                            {job.posted_by.role}
                                        </span>
                                    )}
                                    {job.posted_by.company_name && job.posted_by.company_name !== job.company && (
                                        <span className="opacity-70">· {job.posted_by.company_name}</span>
                                    )}
                                    {job.posted_by.email && job.posted_by.name && (
                                        <a
                                            href={`mailto:${job.posted_by.email}`}
                                            className="opacity-70 hover:opacity-100 hover:underline"
                                        >
                                            · {job.posted_by.email}
                                        </a>
                                    )}
                                </div>
                            ) : job.submitted_by_email ? (
                                <div className="text-xs text-muted-foreground mt-1" data-testid={`admin-job-poster-${job.id}`}>
                                    Posted by <span className="font-medium">{job.submitted_by_email}</span>
                                </div>
                            ) : null}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <a
                                href={`/careers/${job.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`admin-job-view-${job.id}`}
                                className="px-3 py-2 rounded-lg border border-border hover:border-brand-navy text-sm inline-flex items-center gap-1.5"
                            >
                                <ExternalLink size={14} /> View
                            </a>
                            <button
                                onClick={() => copyShareLink(job)}
                                data-testid={`admin-job-share-${job.id}`}
                                className="px-3 py-2 rounded-lg border border-border hover:border-brand-navy text-sm inline-flex items-center gap-1.5"
                            >
                                {copiedId === job.id ? <><CheckCircle2 size={14} className="text-emerald-500" /> Copied</> : <><Copy size={14} /> Share</>}
                            </button>
                            {status !== "published" && (
                                <button
                                    onClick={() => setStatus(job, "published")}
                                    disabled={disabled}
                                    data-testid={`admin-job-publish-${job.id}`}
                                    className="px-3 py-2 rounded-lg border border-emerald-400/50 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10 text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                                    title="Publish to public board"
                                >
                                    <Eye size={14} /> Publish
                                </button>
                            )}
                            {status !== "draft" && (
                                <button
                                    onClick={() => setStatus(job, "draft")}
                                    disabled={disabled}
                                    data-testid={`admin-job-draft-${job.id}`}
                                    className="px-3 py-2 rounded-lg border border-border hover:border-brand-navy text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                                    title="Move to Draft (hidden from public board)"
                                >
                                    <FileEdit size={14} /> Draft
                                </button>
                            )}
                            {status !== "archived" && (
                                <button
                                    onClick={() => setStatus(job, "archived")}
                                    disabled={disabled}
                                    data-testid={`admin-job-archive-${job.id}`}
                                    className="px-3 py-2 rounded-lg border border-border hover:border-brand-navy text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                                    title="Archive (kept for records, hidden from public board)"
                                >
                                    <Archive size={14} /> Archive
                                </button>
                            )}
                            <button
                                onClick={() => setApplicantsJob(job)}
                                data-testid={`admin-job-applicants-${job.id}`}
                                className="px-3 py-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 text-sm inline-flex items-center gap-1.5 transition"
                                title="View candidates who applied to this requisition"
                            >
                                <Users size={14} /> Applicants
                                <span
                                    className="ml-1 tabular-nums font-semibold text-[11px] bg-emerald-500/20 rounded-full px-1.5 py-0.5"
                                    data-testid={`admin-job-applicants-count-${job.id}`}
                                >
                                    {job.applicants_count ?? 0}
                                </span>
                            </button>
                            <button onClick={() => onEdit(job)} data-testid={TID.admin.jobEdit(job.id)} className="px-3 py-2 rounded-lg border border-border hover:border-brand-navy text-sm inline-flex items-center gap-1.5"><Edit3 size={14} /> Edit</button>
                            <button
                                onClick={() => requestDelete(job)}
                                disabled={disabled}
                                data-testid={TID.admin.jobDelete(job.id)}
                                className="px-3 py-2 rounded-lg border border-destructive/30 text-destructive hover:bg-destructive hover:text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                            >
                                <Trash2 size={14} /> Delete
                            </button>
                        </div>
                    </div>
                );
            })}

            {/* Type-DELETE-to-confirm dialog */}
            <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setConfirmText(""); } }}>
                <DialogContent data-testid="admin-jobs-delete-dialog">
                    <DialogHeader>
                        <DialogTitle>Delete this job?</DialogTitle>
                        <DialogDescription>
                            This <strong>permanently deletes</strong> &ldquo;{deleteTarget?.title}&rdquo; and all its applications.
                            To hide the role without removing history, use <strong>Archive</strong> instead.
                            <br /><br />
                            Type <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">DELETE</code> to confirm.
                        </DialogDescription>
                    </DialogHeader>
                    <Input
                        data-testid="admin-jobs-delete-confirm-input"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="DELETE"
                        autoFocus
                    />
                    <DialogFooter className="gap-2">
                        <button
                            type="button"
                            onClick={() => { setDeleteTarget(null); setConfirmText(""); }}
                            data-testid="admin-jobs-delete-cancel"
                            className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={confirmDelete}
                            disabled={confirmText.trim() !== "DELETE" || busyId === deleteTarget?.id}
                            data-testid="admin-jobs-delete-confirm"
                            className="inline-flex items-center gap-1 rounded-full bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
                        >
                            <Trash2 size={12} /> Delete permanently
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <JobApplicantsDrawer
                job={applicantsJob}
                open={!!applicantsJob}
                onClose={() => setApplicantsJob(null)}
            />
        </div>
    );
};

export default JobsTab;
